import type { ApiJob, JobTypeName } from "@tradingdroid/frontends-types";

/** Job statuses that mean the run is over, whatever the outcome. */
export const TERMINAL_JOB_STATUSES = new Set(["completed", "failed", "cancelled"]);

/** Job types that spawn child jobs and answer GET /jobs/{id}/children. */
export const COORDINATOR_JOB_TYPES: readonly JobTypeName[] = ["walk_forward", "monte_carlo"];

export const isCoordinatorJob = (job: ApiJob): boolean =>
  COORDINATOR_JOB_TYPES.includes(job.job_type_name);

/**
 * The subset of a job this module reads. Narrow on purpose: it is everything the progress
 * arithmetic needs and nothing else, so a caller — or a test fixture — can satisfy it without
 * standing up a whole `ApiJob`.
 *
 * Written to distribute over `ApiJob`'s union rather than as a flat interface, so each member keeps
 * its own `result` paired with its own `job_type_name`. A flat `Pick` collapses that correlation
 * and the narrowing on `job_type_name` below stops working.
 */
type ProgressFields<T> = T extends unknown
  ? Pick<T, Extract<keyof T, "job_type_name" | "status_name" | "result">>
  : never;
export type ProgressAwareJob = ProgressFields<ApiJob>;

export interface CoordinatorProgress {
  /** Units that reached a terminal state (finished or failed). */
  done: number;
  /** Units the coordinator planned. */
  total: number;
  failed: number;
  /** What a unit is for this coordinator, already pluralised. */
  unitLabel: string;
  /**
   * The run's own statement that its numbers cover less than it planned.
   *
   * Separate from the counts because the counts cannot always express it: a Monte Carlo child that
   * finishes but contributes no usable value to the distribution is counted as completed, so
   * `done`/`total` reads as complete on a run that aggregated fewer. Only the payload knows.
   */
  partial: boolean;
  /** The reason the payload gave for being incomplete, when it gave one. */
  stopReason?: string;
}

/** The completeness fields a coordinator payload carries when it has something to say. */
interface PartialFields {
  partial?: boolean;
  stop_reason?: string;
}

/**
 * The completeness marker, read only once the run is over.
 *
 * A healthy run in flight also carries `partial: true` — its checkpoints announce themselves as
 * not-yet-a-result — so reading it while the job is running would badge every normal run partial
 * for its whole duration.
 */
const terminalPartial = (
  status: string,
  block: PartialFields | undefined,
): Pick<CoordinatorProgress, "partial" | "stopReason"> =>
  TERMINAL_JOB_STATUSES.has(status) && block?.partial === true
    ? { partial: true, stopReason: block.stop_reason }
    : { partial: false };

// A walk-forward fold is finished once it reaches any of these, matching the backend's own
// progress arithmetic. "completed_no_trades" and "skipped" are finished outcomes, not pending ones.
// "cancelled" is a fold torn down mid-flight whose finished children were banked before it
// unwound: decided, not pending, so a cancelled run does not read as still working through it.
const TERMINAL_FOLD_STATUSES = new Set([
  "completed",
  "completed_no_trades",
  "failed",
  "cancelled",
  "skipped",
]);

/**
 * Child progress a coordinator reports about itself, or null when it has none to report yet.
 *
 * Read from the parent job's own result, so it costs no extra request and stays available after the
 * run finishes. The backend's `running_state` enrichment covers running jobs only.
 *
 * Walk-forward counts folds rather than jobs: one fold spawns several child jobs, and the fold is
 * the unit its result tracks.
 */
export const getCoordinatorProgress = (job: ProgressAwareJob): CoordinatorProgress | null => {
  if (job.job_type_name === "monte_carlo") {
    const monteCarlo = job.result?.data?.monte_carlo;
    const children = monteCarlo?.children;
    if (!children?.total) return null;
    const failed = children.failed ?? 0;
    return {
      done: (children.completed ?? 0) + failed,
      total: children.total,
      failed,
      unitLabel: "children",
      ...terminalPartial(job.status_name, monteCarlo),
    };
  }

  if (job.job_type_name === "walk_forward") {
    const walkForward = job.result?.data?.walk_forward;

    // CPCV runs never populate fold_count: they score combinations over a segment plan instead.
    // Checked first so a CPCV coordinator does not read as "no children" just because it has no folds.
    const cpcv = walkForward?.cpcv;
    const running = !TERMINAL_JOB_STATUSES.has(job.status_name);
    if (running && cpcv?.children?.total) {
      // While the run is in flight, count scoring backtests: they advance one per child, whereas
      // scorable combinations sit at zero until k test segments have landed on a healthy run.
      return {
        done: cpcv.children.completed ?? 0,
        total: cpcv.children.total,
        failed: 0,
        unitLabel: "scoring backtests",
        // Guarded by `running`, so this always reports false. Kept so every branch answers the
        // question the same way rather than by omitting the field.
        ...terminalPartial(job.status_name, cpcv),
      };
    }
    const combinationCount = cpcv?.segment_plan?.n_combinations;
    if (combinationCount) {
      // The run is over, so there is no progress left to track — only how much of the experiment
      // survived. A partial payload reports the combinations it salvaged against the number
      // originally planned, rather than redefining the denominator to look complete. (Its child
      // counts are still there; they are just the wrong question now.)
      // coverage.scored, never combinations.length: a degenerate combination is recorded in the
      // list without being scored, so the length counts work that did not happen.
      return {
        done: cpcv?.coverage?.scored ?? cpcv?.combinations?.length ?? 0,
        total: combinationCount,
        failed: 0,
        unitLabel: "combinations",
        // CPCV states its completeness on its own block; the parent walk_forward one stays unset on
        // a CPCV run.
        ...terminalPartial(job.status_name, cpcv),
      };
    }

    // A CPCV run cancelled or failed before its geometry existed carries the completeness marker
    // and nothing to count: no segment_plan, no children, so both shapes above are skipped and the
    // caller would be told there is nothing to report about a run that says it is incomplete. Read
    // as a fallback rather than ahead of them, so a run that does have counts still reports them.
    const cpcvPartial = terminalPartial(job.status_name, cpcv);
    if (cpcvPartial.partial) {
      return {
        done: cpcv?.coverage?.scored ?? 0,
        total: cpcv?.coverage?.planned ?? 0,
        failed: 0,
        unitLabel: "combinations",
        ...cpcvPartial,
      };
    }

    if (!walkForward?.fold_count) return null;
    const folds = walkForward.folds ?? [];
    const terminal = folds.filter((fold) => TERMINAL_FOLD_STATUSES.has(String(fold.status)));
    return {
      done: terminal.length,
      total: walkForward.fold_count,
      failed: terminal.filter((fold) => fold.status === "failed").length,
      unitLabel: "folds",
      ...terminalPartial(job.status_name, walkForward),
    };
  }

  return null;
};
